import { Subhead } from "../components/SettingsLayout";
import {
  AlertIcon,
  CheckIcon,
  GearIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestIcon,
  LinkExternalIcon,
  RepoPullIcon,
  RepoPushIcon,
} from "@primer/octicons-react";
import { rp } from "../api/client";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAction, useRemote, useWhoami } from "../api/hooks";
import { canIn, type Batch, type BatchState, type Remote as RemoteRow, type RemoteStatus } from "../api/types";
import { Empty, ErrorMessage, Query, RefPill, Sha, Stat, StatsRow, TimeAgo } from "../components/common";
import { forge, type Forge } from "../components/forge";
import { cloneHost, duration } from "../components/format";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, type Tone } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CheckboxField } from "../components/ui/checkbox";
import { Field, FieldGrid } from "../components/ui/field";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tooltip } from "../components/ui/tooltip";

/** Only http(s) URLs become links; the bridge reports pr_url and admins type the remote URL. */
const safeHref = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : undefined);

const BATCH_LABEL: Record<BatchState, { tone: Tone; Icon: typeof GitMergeIcon; text: string }> = {
  pending: { tone: "yellow", Icon: GitPullRequestIcon, text: "Opening" },
  open: { tone: "green", Icon: GitPullRequestIcon, text: "Open" },
  merged: { tone: "purple", Icon: GitMergeIcon, text: "Merged" },
  closed: { tone: "red", Icon: GitPullRequestClosedIcon, text: "Closed" },
};

export function BatchLabel({ state }: { state: BatchState }) {
  const l = BATCH_LABEL[state];
  return (
    <Badge tone={l.tone}>
      <l.Icon size={12} /> {l.text}
    </Badge>
  );
}

export function PrLink({ b, provider }: { b: Batch; provider?: string | null }) {
  if (b.pr_number == null) return <span className="text-muted-foreground">—</span>;
  const href = safeHref(b.pr_url);
  const label = forge(provider).ref(b.pr_number);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {label} <LinkExternalIcon size={12} />
    </a>
  ) : (
    <span>{label}</span>
  );
}

/** The repo's Remote page, linked from Overview and Settings → Remote, like bee admin remote: the remote's state and the batch PR/MR train. It is added and edited under Settings → Remote. */
export function Remote() {
  const { repo = "" } = useParams();
  const q = useRemote(repo);
  const me = useWhoami();
  const admin = canIn(me.data, "admin", repo);
  return (
    <>
      <Subhead title="Remote PRs">Landed work ships to the remote as batch pull requests; commits pushed there are pulled back into main.</Subhead>
      <Query q={q}>{(st) => (st.remote ? <PRRemote repo={repo} st={st} rem={st.remote} admin={admin} /> : <NoPRRemote repo={repo} admin={admin} />)}</Query>
    </>
  );
}

/** No remote: nothing to batch or pull. */
function NoPRRemote({ repo, admin }: { repo: string; admin: boolean }) {
  return (
    <>
      <Empty icon={RepoPushIcon}>
        <b>No remote</b>
        <div className="text-xs text-muted-foreground">
          Add a GitHub remote and bee ships what agents land as batch pull requests, one at a time, and pulls commits humans push back into main.
        </div>
      </Empty>
      <div className="flex items-center justify-center">
        {admin ? (
          <Button asChild variant="primary">
            <Link to={`${rp(repo)}/settings/remote`}>
              <RepoPushIcon /> Add remote
            </Link>
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">An admin can add a remote.</span>
        )}
      </div>
    </>
  );
}

/** Pull and push like bee admin remote pull / push, and the batch train. */
function PRRemote({ repo, st, rem, admin }: { repo: string; st: RemoteStatus; rem: RemoteRow; admin: boolean }) {
  const act = useAction(repo);
  const [done, setDone] = useState<string | null>(null);
  const run = (label: string, path: string) => act.mutate({ path: `/v1/repos/${repo}/remote${path}`, body: {} }, { onSuccess: () => setDone(label) });
  const href = safeHref(rem.url);
  const f = forge(rem.provider);
  return (
    <>
      <Card>
        <CardHeader>
          <f.Icon />
          {href ? (
            <a className="font-mono" href={href} target="_blank" rel="noreferrer noopener">
              {rem.url}
            </a>
          ) : (
            <span className="font-mono">{rem.url}</span>
          )}
          <RefPill className="font-normal">{rem.branch}</RefPill>
          {admin && (
            <span className="ml-auto flex items-center gap-2">
              <Tooltip content={<code>bee admin remote pull</code>}>
                <Button size="sm" disabled={act.isPending} onClick={() => run("Pull queued", "/pull")}>
                  <RepoPullIcon /> Pull
                </Button>
              </Tooltip>
              <Tooltip content={<code>bee admin remote push</code>}>
                <Button size="sm" disabled={act.isPending || !!st.active || st.pending.lands === 0} onClick={() => run("Batch cut", "/push")}>
                  <RepoPushIcon /> Push
                </Button>
              </Tooltip>
              <Tooltip content="Remote settings">
                <Button asChild size="sm" aria-label="Remote settings">
                  <Link to={`${rp(repo)}/settings/remote`}>
                    <GearIcon />
                  </Link>
                </Button>
              </Tooltip>
            </span>
          )}
        </CardHeader>
        <CardContent>
          <StatsRow className="mt-0">
            <Stat n={st.pending.lands} label="lands waiting to push" />
            <Stat n={st.batches.filter((b) => b.state === "merged").length} label="batches merged" />
            <Stat n={rem.pulled_sha ? <Sha repo={repo} sha={rem.pulled_sha} n={7} /> : "—"} label={`last pulled from ${rem.branch}`} />
            <Stat n={rem.checked_at ? <TimeAgo ms={rem.checked_at} /> : "never"} label="last pulled" />
          </StatsRow>
          {rem.error && (
            <Alert variant="warning" className="mt-3.5">
              <AlertTitle>
                <AlertIcon /> {rem.error_code === "conflict" ? `${f.name} and bee conflict` : "The sync hit an error"}
              </AlertTitle>
              <pre className="m-0 whitespace-pre-wrap font-mono text-xs">{rem.error}</pre>
              {rem.error_code === "conflict" ? (
                <div className="mt-2 text-xs">
                  bee opens a batch {f.review} right away so a human can resolve the conflict on {f.name}. Once it merges, bee fast-forwards main.
                </div>
              ) : (
                <div className="mt-2 text-xs">
                  If {f.name} refused access: <PushAccessHint url={rem.url} f={f} admin={admin} /> Then pull again.
                </div>
              )}
            </Alert>
          )}
          {done && !act.error && (
            <Alert variant="success" className="mt-3.5">
              <AlertTitle className="mb-0">
                <CheckIcon /> {done}
              </AlertTitle>
            </Alert>
          )}
          {act.error ? <ErrorMessage error={act.error} /> : null}
        </CardContent>
      </Card>

      {st.active && <ActiveBatch repo={repo} b={st.active} rem={rem} />}

      <Card>
        <CardHeader>
          <GitPullRequestIcon /> Next batch
          <span className="ml-auto text-xs text-muted-foreground">
            {st.blocked ? (
              st.blocked
            ) : st.pending.lands === 0 ? (
              "nothing landed since the last batch"
            ) : st.next_cut ? (
              <>
                cuts at {rem.batch_size} lands or <TimeAgo ms={st.next_cut} />
              </>
            ) : (
              `cuts at ${rem.batch_size} lands`
            )}
          </span>
        </CardHeader>
        {st.next.length ? <LandTable repo={repo} lands={st.next} more={st.pending.lands - st.next.length} /> : null}
      </Card>

      {st.batches.length > 0 && <BatchesCard repo={repo} batches={st.batches} rem={rem} />}
    </>
  );
}

/** The batch PR/MR train, newest first. Shared by the Remote page and Code → Remote PRs. */
export function BatchesCard({ repo, batches, rem }: { repo: string; batches: Batch[]; rem: RemoteRow }) {
  const f = forge(rem.provider);
  return (
    <Card>
      <CardHeader>
        <GitMergeIcon /> Batches
      </CardHeader>
      <Table>
        <TableHeader>
          <tr>
            <TableHead>Batch</TableHead>
            <TableHead>State</TableHead>
            <TableHead>{f.Review}</TableHead>
            <TableHead>Lands</TableHead>
            <TableHead className="max-md:hidden">Head</TableHead>
            <TableHead className="max-md:hidden">Updated</TableHead>
          </tr>
        </TableHeader>
        <TableBody>
          {batches.map((b) => (
            <TableRow key={b.n}>
              <TableCell>
                <span className="font-mono">#{b.n}</span> <span className="text-xs text-muted-foreground max-md:hidden">{b.title}</span>
              </TableCell>
              <TableCell>
                <BatchLabel state={b.state} />
              </TableCell>
              <TableCell>
                <PrLink b={b} provider={rem.provider} />
              </TableCell>
              <TableCell>{b.lands}</TableCell>
              <TableCell className="max-md:hidden">
                <Sha repo={repo} sha={b.merge_sha ?? b.head_sha} n={7} />
              </TableCell>
              <TableCell className="max-md:hidden">
                <TimeAgo ms={b.updated} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ActiveBatch({ repo, b, rem }: { repo: string; b: Batch; rem: RemoteRow }) {
  const f = forge(rem.provider);
  return (
    <Card>
      <CardHeader>
        <BatchLabel state={b.state} /> Batch #{b.n} <PrLink b={b} provider={rem.provider} />
        <span className="ml-auto text-xs text-muted-foreground">
          <RefPill>{b.branch}</RefPill> → <RefPill>{rem.branch}</RefPill>
        </span>
      </CardHeader>
      <CardContent>
        <div>{b.title}</div>
        <div className="text-xs text-muted-foreground">
          {b.lands} land{b.lands === 1 ? "" : "s"} up to <Sha repo={repo} sha={b.head_sha} n={7} />, cut <TimeAgo ms={b.created} />.{" "}
          {b.state === "pending"
            ? `bee is pushing the branch and opening the ${f.review}.`
            : rem.auto_merge
              ? `bee merges it (${rem.merge_method}) once ${f.name} reports it ${f.clean}.`
              : `Waiting for a human to merge it on ${f.name}.`}{" "}
          The next batch starts when this one merges or closes.
        </div>
      </CardContent>
    </Card>
  );
}

function LandTable({ repo, lands, more }: { repo: string; lands: RemoteStatus["next"]; more: number }) {
  return (
    <Table>
      <TableHeader>
        <tr>
          <TableHead>Commit</TableHead>
          <TableHead>Change</TableHead>
          <TableHead className="max-md:hidden">Agent</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead className="max-md:hidden">Landed</TableHead>
        </tr>
      </TableHeader>
      <TableBody>
        {lands.map((l) => (
          <TableRow key={l.seq}>
            <TableCell>
              <Sha repo={repo} sha={l.sha} n={7} />
            </TableCell>
            <TableCell>
              {l.change_id ? (
                <Link className="font-mono text-xs" to={`${rp(repo)}/changes/${l.change_id}`}>
                  {l.change_id}
                </Link>
              ) : (
                <Badge>{l.kind}</Badge>
              )}
            </TableCell>
            <TableCell className="font-mono text-xs max-md:hidden">{l.agent ?? "—"}</TableCell>
            <TableCell>{l.subject}</TableCell>
            <TableCell className="max-md:hidden">
              <TimeAgo ms={l.ts} />
            </TableCell>
          </TableRow>
        ))}
        {more > 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-xs text-muted-foreground">
              …and {more} more
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/** Where the bridge's API token comes from: the repo's bound account (HTTPS only), else BEE_GITHUB_TOKEN. */
function PushAccessHint({ url, f, admin }: { url: string; f: Forge; admin: boolean }) {
  const host = cloneHost(url) ?? "github.com";
  const env = (
    <>
      <code>{f.tokenEnv}</code> on the server
    </>
  );
  if (url && !url.startsWith("https://")) {
    return (
      <>
        Over SSH bee pushes with its own key; opening and merging {f.short}s still needs {env}.
      </>
    );
  }
  return (
    <>
      bee needs a token with {f.tokenNeeds}: <Link to="/profile?tab=account">connect your {host} account</Link>, or set {env}.
    </>
  );
}

/** Edit or remove a remote in mode pr (bee admin remote add / remove). Rendered under Settings → Remote; read-only without the admin action. */
export function PRRemoteForm({ repo, rem, admin, onClose }: { repo: string; rem: RemoteRow; admin: boolean; onClose?: () => void }) {
  const act = useAction(repo);
  const f = forge(rem.provider);
  const [url, setUrl] = useState(rem.url);
  const [branch, setBranch] = useState(rem.branch);
  const [size, setSize] = useState(rem.batch_size);
  const [waitMin, setWaitMin] = useState(Math.round(rem.batch_wait_ms / 60_000));
  const [autoMerge, setAutoMerge] = useState(rem.auto_merge);
  const [method, setMethod] = useState<RemoteRow["merge_method"]>(rem.merge_method);
  const body = {
    url: url.trim(),
    provider: rem.provider,
    branch: branch.trim() || "main",
    batch_size: size,
    batch_wait_s: waitMin * 60,
    auto_merge: autoMerge,
    merge_method: method,
  };
  const methods = [
    { value: "merge", label: "Merge commit (keeps agent SHAs)" },
    { value: "squash", label: "Squash" },
    { value: "rebase", label: "Rebase" },
  ];
  return (
    <Card asChild>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          act.mutate({ method: "PUT", path: `/v1/repos/${repo}/remote`, body }, { onSuccess: onClose });
        }}
      >
        <CardHeader>
          <f.Icon /> Mode pr settings
        </CardHeader>
        <fieldset data-slot="card-content" className="min-w-0 p-4" disabled={!admin}>
          <FieldGrid>
            <Field label={`${f.name} repository URL`} hint={<PushAccessHint url={url.trim()} f={f} admin={admin} />}>
              <Input mono value={url} onChange={(e) => setUrl(e.target.value)} required />
            </Field>
            <Field label="Branch" hint={`Batch ${f.short}s target it; commits pushed to it are pulled into main.`}>
              <Input mono value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
            </Field>
            <Field label="Batch size" hint="Cut a batch once this many lands are waiting.">
              <Input type="number" min={1} max={1000} value={size} onChange={(e) => setSize(Number(e.target.value))} />
            </Field>
            <Field label="Max wait (minutes)" hint={`…or once the oldest land has waited ${duration(waitMin * 60_000) || "0s"}.`}>
              <Input type="number" min={0} max={10080} value={waitMin} onChange={(e) => setWaitMin(Number(e.target.value))} />
            </Field>
          </FieldGrid>
          <CheckboxField
            checked={autoMerge}
            onCheckedChange={(v) => setAutoMerge(v === true)}
            title={`Merge batch ${f.short}s automatically`}
            description={`When ${f.name} reports the ${f.short} ${f.clean} (checks and reviews pass). Otherwise a human merges it.`}
          />
          {autoMerge && (
            <Field label="Merge method" className="mt-3 max-w-60">
              <Select value={method} onValueChange={(v) => setMethod(v as RemoteRow["merge_method"])} options={methods} />
            </Field>
          )}
        </fieldset>
        {admin && (
          <CardContent className="flex items-center gap-2">
            <Button variant="primary" disabled={act.isPending || !url.trim()}>
              Save
            </Button>
            {onClose && (
              <Button type="button" disabled={act.isPending} onClick={onClose}>
                Cancel
              </Button>
            )}
            {act.isSuccess && !onClose && (
              <span className="flex items-center gap-2 text-xs text-success">
                <CheckIcon /> Saved
              </span>
            )}
            <ConfirmDialog
              title={`Remove the remote of ${repo}?`}
              description={
                <>
                  Open batch {f.short}s stay on <code className="break-all">{rem.url}</code>; bee stops pushing and pulling.
                </>
              }
              confirmLabel="Remove"
              onConfirm={() => act.mutate({ method: "DELETE", path: `/v1/repos/${repo}/remote`, body: {} })}
            >
              <Button type="button" variant="danger" className="ml-auto" disabled={act.isPending}>
                Remove remote
              </Button>
            </ConfirmDialog>
          </CardContent>
        )}
        {act.error ? (
          <CardContent>
            <ErrorMessage error={act.error} />
          </CardContent>
        ) : null}
      </form>
    </Card>
  );
}
